import { useState, useEffect, useCallback, useRef } from 'react';
import { getStoredUserId } from '../utils/analytics';
import { WS_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook for Hyperliquid positions WebSocket connection
 * Following useWebSocket.js pattern
 *
 * @param {Object} filters - Position filters
 * @param {number} filters.minPositionSize - Minimum position size in USD
 * @param {string} filters.side - 'all', 'long', or 'short'
 * @param {Array} filters.symbols - Array of symbols to filter (optional)
 * @param {string} filters.userSearch - User address search (optional)
 * @param {Object} options - Additional options
 * @param {boolean} options.enabled - Whether to enable WebSocket (default: true)
 */
export function useHyperliquidPositions(filters, options = {}) {
  const { enabled = true } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [positions, setPositions] = useState([]);
  const [longShortStats, setLongShortStats] = useState({ longCount: 0, shortCount: 0, longValue: 0, shortValue: 0 });

  const wsRef = useRef(null);
  const reconnectDelayRef = useRef(1000);
  const maxReconnectDelay = 30000;
  const isConnectingRef = useRef(false);
  const shouldReconnectRef = useRef(true);
  const reconnectTimeoutRef = useRef(null);
  const filtersRef = useRef(filters);
  const previousFiltersRef = useRef(filters);
  const debounceTimerRef = useRef(null);

  // Update filters ref
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Build filter payload from filters object
  const buildFilterPayload = useCallback((f) => {
    const payload = {
      minPositionSize: f.minPositionSize,
      side: f.side
    };
    if (f.symbols && f.symbols.length > 0) {
      payload.symbols = f.symbols;
    }
    if (f.userSearch && f.userSearch.trim()) {
      payload.userSearch = f.userSearch.trim();
    }
    return payload;
  }, []);

  // Subscribe to positions feed with filters
  const subscribe = useCallback((ws, filters) => {
    if (ws.readyState !== WebSocket.OPEN) return;

    ws.send(JSON.stringify({
      type: 'subscribe',
      filters: buildFilterPayload(filters)
    }));
  }, [buildFilterPayload]);

  // Update filters (send update_filters message)
  const updateFilters = useCallback((newFilters) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(JSON.stringify({
      type: 'update_filters',
      filters: buildFilterPayload(newFilters)
    }));
  }, [buildFilterPayload]);

  // Auto-update filters when they change (with 300ms debounce)
  useEffect(() => {
    if (!isConnected || !isSubscribed) return;

    const prevFilters = previousFiltersRef.current;
    const hasChanged =
      prevFilters.minPositionSize !== filters.minPositionSize ||
      prevFilters.side !== filters.side ||
      prevFilters.userSearch !== filters.userSearch ||
      JSON.stringify(prevFilters.symbols || []) !== JSON.stringify(filters.symbols || []);

    if (hasChanged) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        updateFilters(filters);
        previousFiltersRef.current = filters;
      }, 300);
    }
  }, [filters.minPositionSize, filters.side, filters.userSearch, filters.symbols, isConnected, isSubscribed, updateFilters]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (isConnectingRef.current || (wsRef.current && wsRef.current.readyState === WebSocket.OPEN)) {
      return;
    }

    isConnectingRef.current = true;

    const isDev = window.location.port === '5173' || window.location.port === '5174' || window.location.port === '5175';
    const basePath = '/hyperliquid-positions';
    const baseUrl = isDev
      ? (WS_BASE_URL ? `${WS_BASE_URL}${basePath}` : `ws://localhost:${window.location.port}${basePath}`)
      : `${WS_BASE_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`}${basePath}`;

    const userId = getStoredUserId();
    const wsUrl = `${baseUrl}?userId=${encodeURIComponent(userId)}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setSubscriptionError(null);
      reconnectDelayRef.current = 1000;
      isConnectingRef.current = false;
      subscribe(ws, filtersRef.current);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case 'connection':
            break;

          case 'subscription_success':
            setIsSubscribed(true);
            setSubscriptionError(null);
            break;

          case 'subscription_error':
            console.error('Subscription error:', message.error);
            setSubscriptionError(message.error);
            setIsSubscribed(false);
            break;

          case 'historical_positions':
            setPositions(message.data.positions || []);
            break;

          case 'position_opened':
            setPositions(prev => {
              const exists = prev.some(p => p.user === message.data.user && p.symbol === message.data.symbol);
              if (exists) {
                return prev.map(p =>
                  (p.user === message.data.user && p.symbol === message.data.symbol)
                    ? { ...message.data }
                    : p
                );
              }
              return [message.data, ...prev].slice(0, 200);
            });
            break;

          case 'position_closed':
            setPositions(prev => prev.filter(p =>
              !(p.user === message.data.user && p.symbol === message.data.symbol)
            ));
            break;

          case 'position_update':
            setPositions(prev => prev.map(p =>
              (p.user === message.data.user && p.symbol === message.data.symbol)
                ? { ...message.data }
                : p
            ));
            break;

          case 'long_short_stats':
            setLongShortStats(message.data);
            break;

          case 'filter_update_success':
            break;

          case 'error':
            console.error('WebSocket error:', message.message);
            break;

          case 'pong':
            break;

          default:
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = () => {
      isConnectingRef.current = false;
    };

    ws.onclose = () => {
      setIsConnected(false);
      setIsSubscribed(false);
      wsRef.current = null;
      isConnectingRef.current = false;

      if (shouldReconnectRef.current && enabled) {
        const delay = Math.min(reconnectDelayRef.current, maxReconnectDelay);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, maxReconnectDelay);
          connect();
        }, delay);
      }
    };
  }, [subscribe, enabled]);

  // Connect on mount if enabled
  useEffect(() => {
    if (!enabled) return;

    shouldReconnectRef.current = true;
    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [enabled, connect]);

  // Ping every 30 seconds
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  const simulatePositionOpened = useCallback((position) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'simulate_position_opened', data: position }));
    }
  }, []);

  const simulatePositionUpdate = useCallback((position) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'simulate_position_update', data: position }));
    }
  }, []);

  const simulatePositionClosed = useCallback((position) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'simulate_position_closed', data: position }));
    }
  }, []);

  return {
    isConnected,
    isSubscribed,
    subscriptionError,
    positions,
    longShortStats,
    updateFilters,
    simulatePositionOpened,
    simulatePositionUpdate,
    simulatePositionClosed
  };
}
